"use client";

/**
 * P11-11 · The /contact form.
 *
 * Follows the newsletter form's conventions rather than inventing new ones: an
 * off-screen honeypot, a success state that REPLACES the form and takes focus,
 * and `aria-busy` while in flight.
 *
 * Focus management is the part worth stating: on success the form unmounts and
 * the confirmation mounts in its place, so without moving focus a keyboard user
 * is dropped to `<body>` with no announcement that anything happened. A live
 * region that mounts at the same moment it should announce is unreliable —
 * hence the explicit `focus()`.
 */
import * as React from "react";

import { api } from "~/trpc/react";
import { useTranslator } from "~/i18n/provider";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]";
const labelClass = "flex flex-col gap-1 text-sm font-medium text-fg";

export function ContactForm() {
  const t = useTranslator();
  const uid = React.useId();
  const errorId = `${uid}-error`;

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [orderNumber, setOrderNumber] = React.useState("");
  const [body, setBody] = React.useState("");
  const [website, setWebsite] = React.useState(""); // honeypot
  const [clientError, setClientError] = React.useState<string | null>(null);

  const submit = api.contact.submit.useMutation();

  const successRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (submit.isSuccess) successRef.current?.focus();
  }, [submit.isSuccess]);

  if (submit.isSuccess) {
    return (
      <div
        ref={successRef}
        role="status"
        aria-live="polite"
        tabIndex={-1}
        className="rounded-lg border border-success/40 bg-success/10 p-4 outline-none"
      >
        <p className="text-sm font-semibold text-fg">{t("contact.received")}</p>
        <p className="mt-1 text-sm text-fg-muted">
          We&rsquo;ll reply to {email} as soon as we can, usually within one
          working day.
        </p>
      </div>
    );
  }

  const error = clientError ?? (submit.isError ? submit.error.message : null);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setClientError(null);
    // Client-side checks mirror the server's, purely so the common mistakes
    // don't cost a round trip. The server re-validates regardless.
    if (name.trim().length === 0) {
      setClientError("Please tell us your name.");
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setClientError("Please enter a valid email address so we can reply.");
      return;
    }
    if (body.trim().length === 0) {
      setClientError("Please write your message.");
      return;
    }
    submit.mutate({
      name: name.trim(),
      email: email.trim(),
      ...(orderNumber.trim() ? { orderNumber: orderNumber.trim() } : {}),
      body: body.trim(),
      website,
    });
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={t("contact.title")}
      // `noValidate`: the browser's own bubble would fire FIRST on the
      // `type="email"` field and block this handler, so our styled, single,
      // `role="alert"` message would never appear. Native bubbles are also
      // unstyleable and inconsistent between browsers. `required` stays for
      // assistive tech; the validation itself lives in one place, here — and
      // the server re-validates regardless.
      noValidate
      className="grid gap-4"
    >
      <label className={labelClass}>
        {t("qa.yourName")}
        <input
          type="text"
          required
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-describedby={error ? errorId : undefined}
          className={inputClass}
        />
      </label>

      <label className={labelClass}>
        Email
        <input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-describedby={error ? errorId : undefined}
          className={inputClass}
        />
      </label>

      <label className={labelClass}>
        Order number{" "}
        <span className="font-normal text-fg-muted">(optional)</span>
        <input
          type="text"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          className={inputClass}
        />
      </label>

      <label className={labelClass}>
        Message
        <textarea
          required
          rows={6}
          maxLength={5000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          aria-describedby={error ? errorId : undefined}
          className={inputClass}
        />
      </label>

      {/* Honeypot: off-screen, non-tabbable; a filled value marks a bot. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        className="absolute left-[-9999px] top-0 h-0 w-0 opacity-0"
      />

      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div>
        <button
          type="submit"
          disabled={submit.isPending}
          aria-busy={submit.isPending || undefined}
          className="rounded-[var(--btn-radius)] bg-primary px-5 py-2.5 text-sm font-semibold text-primary-fg hover:opacity-90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          {submit.isPending ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
}
